// frontend/src/pages/superAdmin/ManageFiscalYears.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../services/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';

const ManageFiscalYears = ({ user, setUser }) => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newYear, setNewYear] = useState('');

  // Fetch fiscal years data
  const fetchFiscalYears = async () => {
    try {
      setLoading(true);
      const response = await api.get('/fiscal-years');
      setFiscalYears(response.data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลปีงบประมาณได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    setNewYear(e.target.value);
  };

  // Open add modal
  const handleAddClick = () => {
    setNewYear('');
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create fiscal year
      await api.post('/fiscal-years', { year: newYear });
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'เพิ่มปีงบประมาณเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง'
      });
      
      // Refresh fiscal years list
      fetchFiscalYears();
      setShowModal(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // Set active fiscal year
  const handleSetActive = async (id) => {
    try {
      await Swal.fire({
        title: 'ยืนยันการเปลี่ยนปีงบประมาณ',
        text: 'คุณต้องการตั้งเป็นปีงบประมาณปัจจุบันใช่หรือไม่?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await api.put(`/fiscal-years/${id}/active`);
          
          Swal.fire({
            icon: 'success',
            title: 'สำเร็จ',
            text: 'ตั้งเป็นปีงบประมาณปัจจุบันเรียบร้อยแล้ว',
            confirmButtonText: 'ตกลง'
          });
          
          // Refresh fiscal years list
          fetchFiscalYears();
        }
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถตั้งเป็นปีงบประมาณปัจจุบันได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการปีงบประมาณ" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการปีงบประมาณ</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">จัดการปีงบประมาณ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            <div className="card card-navy">
              <div className="card-header">
                <h3 className="card-title">รายการปีงบประมาณในระบบ</h3>
                <div className="card-tools">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleAddClick}
                  >
                    <i className="fas fa-plus"></i> เพิ่มปีงบประมาณ
                  </button>
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr className="bg-navy">
                          <th style={{ width: '5%' }}>ลำดับ</th>
                          <th style={{ width: '30%' }}>ปีงบประมาณ</th>
                          <th style={{ width: '20%' }}>สถานะ</th>
                          <th style={{ width: '15%' }}>วันที่สร้าง</th>
                          <th style={{ width: '15%' }}>จัดการ</th>
                          <th style={{ width: '15%' }}>ตั้งค่าระบบ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fiscalYears.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center">
                              ไม่พบข้อมูลปีงบประมาณ
                            </td>
                          </tr>
                        ) : (
                          fiscalYears.map((year, index) => (
                            <tr key={year.id}>
                              <td className="text-center">{index + 1}</td>
                              <td>{year.year}</td>
                              <td>
                                {year.active ? (
                                  <span className="badge bg-success">ปีงบประมาณปัจจุบัน</span>
                                ) : (
                                  <span className="badge bg-secondary">ไม่ได้ใช้งาน</span>
                                )}
                              </td>
                              <td>
                                {new Date(year.created_at).toLocaleDateString('th-TH')}
                              </td>
                              <td className="text-center">
                                {!year.active && (
                                  <button
                                    className="btn btn-warning btn-sm"
                                    onClick={() => handleSetActive(year.id)}
                                  >
                                    <i className="fas fa-check-circle"></i> ตั้งเป็นปีปัจจุบัน
                                  </button>
                                )}
                              </td>
                              <td className="text-center">
                                <Link
                                  to={`/super-admin/system-settings?year=${year.id}`}
                                  className="btn btn-info btn-sm"
                                >
                                  <i className="fas fa-cogs"></i> ตั้งค่าระบบ
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Modal for Add Fiscal Year */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-navy">
                <h5 className="modal-title">เพิ่มปีงบประมาณ</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="year" className="form-label">
                      ปีงบประมาณ (พ.ศ.) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="year"
                      name="year"
                      value={newYear}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9]{4}"
                      minLength="4"
                      maxLength="4"
                      placeholder="เช่น 2567"
                      title="กรุณากรอกตัวเลข 4 หลัก"
                    />
                    <div className="form-text">กรอกเฉพาะตัวเลข 4 หลัก เช่น 2567</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    เพิ่มปีงบประมาณ
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageFiscalYears;